import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiService } from "../lib/api";
import {
  getPeriodOptions,
  readPeriodParam,
  setPeriodParam,
  withPeriod,
} from "../lib/periods";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Loader2, ArrowLeft, Calendar, TrendingUp, TrendingDown, Building2,
  Layers, Tag, AlertTriangle, Receipt,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Cell, Legend, ComposedChart, Line,
} from "recharts";

const COLORS = ['#ef4444', '#f59e0b', '#8b5cf6', '#3b82f6', '#06b6d4', '#ec4899', '#84cc16', '#10b981'];

const ExpensesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [expenses, setExpenses] = useState([]);
  // The period lives in the query string so it survives navigation.
  const selectedQuarter = readPeriodParam(searchParams);
  const handlePeriodChange = (value) =>
    setSearchParams(setPeriodParam(searchParams, value), { replace: true });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const quarter = selectedQuarter === "all" ? undefined : selectedQuarter;
      const [dash, detail] = await Promise.all([
        apiService.getDashboardAnalytics(quarter),
        apiService.getExpensesDetail(quarter, 50),
      ]);
      setAnalytics(dash.data);
      setExpenses(detail.data.expenses || []);
    } catch (error) {
      console.error("Failed to fetch expenses:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedQuarter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", {
      style: "currency", currency: "EUR",
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(amount || 0);

  // Each row is formatted in its own currency: formatCurrency() is EUR-only, so
  // using it for a USD row produced the mixed label "USD €5,000".
  const formatAmountInCurrency = (amount, currency) => {
    const code = (currency || "EUR").toUpperCase();
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency", currency: code,
        minimumFractionDigits: 0, maximumFractionDigits: 0,
      }).format(amount || 0);
    } catch {
      // An unknown code would make Intl throw, so fall back to a plain number.
      return `${code} ${new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0, maximumFractionDigits: 0,
      }).format(amount || 0)}`;
    }
  };

  const formatCurrencyShort = (value) => {
    if (value >= 1000000) return `€${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `€${(value / 1000).toFixed(1)}K`;
    return `€${value?.toFixed(0) || 0}`;
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
      });
    } catch {
      return iso;
    }
  };

  // One shared option list for every analytics page (see lib/periods.js).
  const periodOptions = getPeriodOptions();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const totalSpend = analytics?.total_expense || 0;
  const fixed = analytics?.fixed_expenses || 0;
  const variable = analytics?.variable_expenses || 0;
  const fixedVariableBase = fixed + variable;
  const fixedPct = fixedVariableBase > 0 ? (fixed / fixedVariableBase) * 100 : 0;
  // Expenses without a counterparty name cannot be split into fixed or variable.
  const unattributed = analytics?.fixed_variable_unattributed || 0;
  const intercompany = analytics?.intercompany_transfers || { amount: 0, count: 0, name: "" };
  const topCategory = analytics?.spend_by_category?.[0];
  const topVendor = analytics?.all_vendors?.[0];

  // Monthly spend (fixed vs variable stacked) from the last months.
  const monthlyData = (analytics?.spend_by_category_monthly || []).map((m) => {
    const parts = String(m.month).split(" ");
    const short = (parts[0]?.substring(0, 3) || m.month) + " " + (parts[1]?.substring(2) || "");
    return { month: short, fixed: m.fixed || 0, variable: m.variable || 0 };
  });

  // Category breakdown (horizontal bars).
  const categoryData = (analytics?.spend_by_category || []).slice(0, 12).map((c, i) => ({
    name: c.category,
    shortName: c.category.length > 18 ? c.category.substring(0, 18) + "…" : c.category,
    value: c.amount,
    color: COLORS[i % COLORS.length],
  }));

  // Pareto: vendors sorted desc with cumulative share of vendor spend.
  const vendors = analytics?.all_vendors || [];
  const vendorTotal = vendors.reduce((s, v) => s + v.amount, 0) || 1;
  let cumulative = 0;
  const paretoData = vendors.slice(0, 15).map((v) => {
    cumulative += v.amount;
    return {
      name: v.name.length > 14 ? v.name.substring(0, 14) + "…" : v.name,
      fullName: v.name,
      amount: v.amount,
      cumulativePct: Math.round((cumulative / vendorTotal) * 1000) / 10,
    };
  });
  // How many vendors make up 80% of spend.
  let running = 0;
  let vendors80 = 0;
  for (const v of vendors) {
    running += v.amount;
    vendors80 += 1;
    if (running / vendorTotal >= 0.8) break;
  }

  const kpis = [
    { label: "Total Spend", value: formatCurrencyShort(totalSpend), icon: Receipt, color: "red" },
    {
      label: "Month-over-Month", icon: analytics?.burn_rate_change > 0 ? TrendingUp : TrendingDown,
      value: `${analytics?.burn_rate_change > 0 ? "+" : ""}${analytics?.burn_rate_change || 0}%`,
      color: analytics?.burn_rate_change > 0 ? "red" : "emerald",
    },
    { label: "Fixed of operating", value: `${fixedPct.toFixed(0)}%`, icon: Layers, color: "blue",
      sub: `${formatCurrencyShort(fixed)} fixed / ${formatCurrencyShort(variable)} variable`,
      sub2: unattributed > 0
        ? `${formatCurrencyShort(unattributed)} unattributed (no counterparty, not split)`
        : null },
    { label: "Top Category", value: topCategory ? formatCurrencyShort(topCategory.amount) : "—",
      icon: Tag, color: "violet", sub: topCategory?.category },
    { label: "Vendor Concentration", value: topVendor ? `${topVendor.percentage}%` : "—",
      icon: AlertTriangle, color: topVendor?.percentage > 25 ? "amber" : "slate",
      sub: topVendor ? `${topVendor.name} is #1` : "" },
    { label: "Intercompany Funding", value: formatCurrencyShort(intercompany.amount),
      icon: Building2, color: "indigo", sub: `${intercompany.count} transfers to satellite` },
  ];

  return (
    <div className="space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(withPeriod("/analytics", selectedQuarter))} className="h-9 w-9 p-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100">
                <Receipt className="w-6 h-6 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">Expenses &amp; Vendors</h1>
            </div>
            <p className="text-slate-500 mt-1 ml-12">
              Where the money goes: categories, vendor concentration and the largest line items
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <Select value={selectedQuarter} onValueChange={handlePeriodChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Time" />
            </SelectTrigger>
            <SelectContent>
              {periodOptions.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {kpis.map((k, idx) => {
          const Icon = k.icon;
          return (
            <Card key={idx} className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{k.label}</p>
                  <Icon className={`w-4 h-4 text-${k.color}-500`} />
                </div>
                <p className={`text-2xl font-bold mt-1 text-${k.color}-600`}>{k.value}</p>
                {k.sub && <p className="text-xs text-slate-400 mt-1 truncate">{k.sub}</p>}
                {k.sub2 && <p className="text-xs text-slate-400 truncate">{k.sub2}</p>}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Intercompany callout */}
      {intercompany.amount > 0 && (
        <Card className="border-0 shadow-sm bg-indigo-50">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-indigo-500 mt-0.5" />
              <div>
                <h4 className="font-medium text-slate-900">{intercompany.name}</h4>
                <p className="text-sm text-slate-600 mt-1">
                  {formatCurrency(intercompany.amount)} across {intercompany.count} transfers funds the
                  development team at the satellite company. This is intercompany funding, not third-party
                  vendor spend, so it is kept out of the vendor ranking below.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly spend trend + category breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Monthly operating spend (fixed vs variable)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="month" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={10} tickFormatter={formatCurrencyShort} />
                <RechartsTooltip formatter={(v) => formatCurrency(v)} />
                <Legend />
                <Bar dataKey="fixed" stackId="a" fill="#3b82f6" name="Fixed" radius={[0, 0, 0, 0]} />
                <Bar dataKey="variable" stackId="a" fill="#8b5cf6" name="Variable" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Spend by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={categoryData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" stroke="#64748b" fontSize={10} tickFormatter={formatCurrencyShort} />
                <YAxis type="category" dataKey="shortName" stroke="#64748b" fontSize={10} width={120} />
                <RechartsTooltip formatter={(v) => formatCurrency(v)} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {categoryData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Vendor concentration (Pareto) */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium text-slate-700">Vendor Concentration (80/20)</CardTitle>
            <Badge variant="secondary" className="text-xs">
              {vendors80} vendor{vendors80 === 1 ? "" : "s"} = 80% of spend
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={paretoData} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="name" stroke="#64748b" fontSize={10} angle={-35} textAnchor="end" interval={0} height={60} />
              <YAxis yAxisId="left" stroke="#64748b" fontSize={10} tickFormatter={formatCurrencyShort} />
              <YAxis yAxisId="right" orientation="right" stroke="#f59e0b" fontSize={10} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <RechartsTooltip
                formatter={(v, name) => (name === "Cumulative %" ? `${v}%` : formatCurrency(v))}
                labelFormatter={(_, p) => p?.[0]?.payload?.fullName || ""}
              />
              <Bar yAxisId="left" dataKey="amount" fill="#ef4444" name="Spend" radius={[4, 4, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="cumulativePct" stroke="#f59e0b" strokeWidth={2} name="Cumulative %" dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Largest transactions */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-700">Largest Expenses</CardTitle>
          <p className="text-xs text-slate-400 mt-1">
            Ranked by the EUR amount shown. A row booked in another currency shows the
            original beneath it. Where a credit was booked against the expense in this
            period, the net is shown too.
          </p>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[360px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">#</TableHead>
                  <TableHead className="text-xs">Date</TableHead>
                  <TableHead className="text-xs">Vendor</TableHead>
                  <TableHead className="text-xs">Category</TableHead>
                  <TableHead className="text-xs text-right">Refunded</TableHead>
                  <TableHead className="text-xs text-right">Amount (EUR)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((e, idx) => (
                  <TableRow key={e.id || idx} className="text-xs">
                    <TableCell className="py-1.5 text-slate-400">{idx + 1}</TableCell>
                    <TableCell className="py-1.5 text-slate-500 whitespace-nowrap">{formatDate(e.date)}</TableCell>
                    <TableCell className="py-1.5 font-medium text-slate-700">
                      {e.vendor}
                      {e.is_intercompany && (
                        <Badge variant="secondary" className="ml-2 text-[10px] bg-indigo-100 text-indigo-700">intercompany</Badge>
                      )}
                    </TableCell>
                    <TableCell className="py-1.5 text-slate-500">{e.category}</TableCell>
                    <TableCell className="py-1.5 text-right text-emerald-600 whitespace-nowrap">
                      {e.credited_in_period_eur
                        ? `−${formatCurrency(e.credited_in_period_eur)}`
                        : <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell className="py-1.5 text-right whitespace-nowrap">
                      <span className="font-semibold text-red-500">{formatCurrency(e.amount_eur)}</span>
                      {e.currency && e.currency !== "EUR" && (
                        <div className="text-[10px] text-slate-400">
                          {formatAmountInCurrency(e.amount, e.currency)}
                        </div>
                      )}
                      {e.credited_in_period_eur > 0 && (
                        <div className="text-[10px] text-slate-500">
                          net {formatCurrency(e.net_amount_eur)}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {expenses.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-slate-400">No expenses in this period</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExpensesPage;
